// Phase I0.5 — useTodayFixtures hook.
//
// Reads the next 24 hours of active fixtures from `vw_pipeline_fixtures`
// (flat view over canonical_fixtures + teams + competitions). Returns
// rows shaped like the prototype's TODAY_FIXTURES element so the
// DailyFixtureRail can render the same way against real data.
//
// Doctrine: never fabricate. Empty result → `fixtures: []`. The hook is
// auth-gated for now (Daily Fixture Rail will be mounted on auth-gated
// routes); RLS on canonical_fixtures permits authenticated read.
// Badge URLs are sourced from `teams.badge_url` (curated Wikimedia
// Commons SVG manifest, see migration 20260510110000_teams_badge_url).
// Teams without a curated badge return `null`; <TeamLine>'s onError
// kit-colour-monogram fallback picks up that case (CLAUDE.md hard rule).

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { kitColor } from "./kit-colors";

export type TodayFixtureStatus = "upcoming" | "live" | "settled";

export interface TodayFixture {
  id: string;
  fixtureId: string;
  league: string;
  leagueShort: string;
  homeShort: string;
  awayShort: string;
  home: string;
  away: string;
  homeKit: string;
  awayKit: string;
  homeBadge: string | null;
  awayBadge: string | null;
  /** Local-time HH:MM kickoff label, derived from kickoff_at. */
  kickoff: string;
  kickoffISO: string;
  status: TodayFixtureStatus;
  liveScore?: string;
  finalScore?: string;
  minute?: number;
}

export interface TodayFixturesState {
  fixtures: TodayFixture[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const FALLBACK_SHORT = (full: string): string => {
  if (!full) return "—";
  const cleaned = full
    .replace(/[^A-Za-z\s]/g, "")
    .trim()
    .toUpperCase();
  if (cleaned.length <= 3) return cleaned.padEnd(3, "·").slice(0, 3);
  // Take first 3 letters of the first significant word.
  const word = cleaned.split(/\s+/)[0] ?? cleaned;
  return word.slice(0, 3);
};

function mapStatus(raw: string | null | undefined): TodayFixtureStatus {
  if (!raw) return "upcoming";
  const v = raw.toLowerCase();
  if (v === "live" || v === "in_play" || v === "1h" || v === "2h" || v === "ht") return "live";
  if (v === "ft" || v === "settled" || v === "finished" || v === "completed") return "settled";
  return "upcoming";
}

function hhmm(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const h = String(d.getUTCHours()).padStart(2, "0");
  const m = String(d.getUTCMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

function normaliseRow(row: Record<string, unknown>): TodayFixture | null {
  const fixtureId = typeof row.fixture_id === "string" ? row.fixture_id : null;
  const home = typeof row.home_team === "string" ? row.home_team : null;
  const away = typeof row.away_team === "string" ? row.away_team : null;
  const kickoffISO = typeof row.kickoff_at === "string" ? row.kickoff_at : null;
  if (!fixtureId || !home || !away || !kickoffISO) return null;

  const league =
    (typeof row.league_name === "string" && row.league_name) ||
    (typeof row.competition === "string" && row.competition) ||
    "—";
  const leagueShort =
    (typeof row.league_short === "string" && row.league_short) ||
    (typeof row.competition_code === "string" && row.competition_code) ||
    league.slice(0, 3).toUpperCase();
  const homeShort = FALLBACK_SHORT(home);
  const awayShort = FALLBACK_SHORT(away);
  const status = mapStatus(typeof row.fixture_status === "string" ? row.fixture_status : null);
  const homeBadge = typeof row.home_badge_url === "string" && row.home_badge_url ? row.home_badge_url : null;
  const awayBadge = typeof row.away_badge_url === "string" && row.away_badge_url ? row.away_badge_url : null;

  return {
    id: fixtureId,
    fixtureId,
    league,
    leagueShort,
    homeShort,
    awayShort,
    home,
    away,
    homeKit: kitColor(homeShort),
    awayKit: kitColor(awayShort),
    homeBadge,
    awayBadge,
    kickoff: hhmm(kickoffISO),
    kickoffISO,
    status,
  };
}

export function useTodayFixtures(): TodayFixturesState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [fixtures, setFixtures] = useState<TodayFixture[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    const now = new Date();
    const horizon = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    // db/types.ts may not include vw_pipeline_fixtures — cast.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const res = await sb
      .from("vw_pipeline_fixtures")
      .select(
        "fixture_id, home_team, away_team, home_badge_url, away_badge_url, league_name, league_short, competition, competition_code, kickoff_at, fixture_status, is_active",
      )
      .eq("is_active", true)
      .gte("kickoff_at", now.toISOString())
      .lte("kickoff_at", horizon.toISOString())
      .order("kickoff_at", { ascending: true });

    if (res.error) {
      setError(res.error.message ?? "Failed to load fixtures");
      setFixtures([]);
      setLoading(false);
      return;
    }

    const rows: unknown[] = Array.isArray(res.data) ? res.data : [];
    const out: TodayFixture[] = [];
    for (const r of rows) {
      if (r && typeof r === "object") {
        const f = normaliseRow(r as Record<string, unknown>);
        if (f) out.push(f);
      }
    }
    setFixtures(out);
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<TodayFixturesState>(
    () => ({ fixtures, loading, error, refetch }),
    [fixtures, loading, error, refetch],
  );
}
